import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createTeam, ensureProfile, getState, getStoredDisplayName, joinTeam, pressYatta, saveDisplayName, sendFeedback, useMakeup } from "./api";
import { installQueuedClipRetry, playEventSound, playSound, preloadEventSounds } from "./audio";
import type { EventSoundCategory } from "./soundManifest";
import { getShortDate } from "./date";
import type { AppState } from "./types";
import { enablePushNotifications, getPushCapabilityStatus, refreshPushSubscription, type PushStatus } from "./push";

type FxMode = "normal" | "big" | "mega";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  maxLife: number;
  color: string;
  shape: "dot" | "spark" | "slash" | "ring";
  spin: number;
  angle: number;
}

const COLORS = ["#e10600", "#ff2e88", "#ffd400", "#00e5ff", "#6a00ff", "#f8faff"];
const POLL_MS = 5000;

function useCanvasFx() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number | null>(null);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  const tick = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    const particles = particlesRef.current;

    for (let i = particles.length - 1; i >= 0; i -= 1) {
      const p = particles[i];
      p.life += 1;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.12;
      p.angle += p.spin;

      const alpha = Math.max(0, 1 - p.life / p.maxLife);
      ctx.globalAlpha = alpha;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);

      if (p.shape === "slash") {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = Math.max(2, p.r * 0.65);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(-p.r * 3, 0);
        ctx.lineTo(p.r * 3, 0);
        ctx.stroke();
      } else if (p.shape === "spark") {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = Math.max(1.5, p.r * 0.45);
        ctx.beginPath();
        ctx.moveTo(-p.r * 2.2, 0);
        ctx.lineTo(p.r * 2.2, 0);
        ctx.moveTo(0, -p.r * 2.2);
        ctx.lineTo(0, p.r * 2.2);
        ctx.stroke();
      } else if (p.shape === "ring") {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = Math.max(2, p.r * 0.28);
        ctx.beginPath();
        ctx.arc(0, 0, p.r * (0.8 + (1 - alpha) * 4.2), 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.fillStyle = p.color;
        ctx.arc(0, 0, p.r * (0.6 + alpha * 0.7), 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();

      if (p.life >= p.maxLife) particles.splice(i, 1);
    }

    ctx.globalAlpha = 1;
    if (particles.length > 0) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      rafRef.current = null;
    }
  }, []);

  const burst = useCallback(
    (mode: FxMode, origin?: { x: number; y: number }) => {
      const x = origin?.x ?? window.innerWidth / 2;
      const y = origin?.y ?? window.innerHeight * 0.32;
      const count = mode === "mega" ? 520 : mode === "big" ? 360 : 120;
      const speed = mode === "mega" ? 11.5 : mode === "big" ? 9.2 : 4.8;
      const maxLife = mode === "mega" ? 110 : mode === "big" ? 86 : 52;

      const rings = mode === "mega" ? 8 : mode === "big" ? 5 : 2;
      for (let i = 0; i < rings; i += 1) {
        particlesRef.current.push({
          x,
          y,
          vx: 0,
          vy: 0,
          r: 10 + i * 7,
          life: 0,
          maxLife: maxLife + i * 5,
          color: COLORS[i % COLORS.length],
          shape: "ring",
          spin: 0,
          angle: 0,
        });
      }

      for (let i = 0; i < count; i += 1) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.55;
        const sp = speed * (0.7 + Math.random() * 0.8);
        const shapeRoll = Math.random();
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(angle) * sp,
          vy: Math.sin(angle) * sp - (mode === "mega" ? 2.1 : 1.4),
          r: 1.6 + Math.random() * (mode === "normal" ? 3.4 : 5.8),
          life: 0,
          maxLife: maxLife + Math.floor(Math.random() * 12),
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          shape: shapeRoll > 0.76 ? "slash" : shapeRoll > 0.48 ? "spark" : "dot",
          spin: (Math.random() - 0.5) * 0.32,
          angle: Math.random() * Math.PI * 2,
        });
      }

      if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    },
    [tick]
  );

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [resize]);

  return { canvasRef, burst };
}

function getCelebrationMode(count = 0, size = 0): FxMode {
  if (size === 4 && count >= 4) return "mega";
  if ((size === 4 && count >= 3) || (size === 3 && count >= 3)) return "big";
  return "big";
}

function isBeforeEight(now = new Date()): boolean {
  return now.getHours() < 8;
}

function getDoneCountForDay(state: AppState, day: string): number {
  const members = state.team?.members.map((member) => member.profileId) ?? [state.profile.id];
  return members.filter((profileId) => state.statuses[profileId]?.includes(day)).length;
}

function hasFourDaySquare(state: AppState): boolean {
  if (!state.team || state.team.members.length !== 4) return false;
  const days = state.days.slice(0, 4);
  return days.length === 4 && days.every((day) => getDoneCountForDay(state, day) === 4);
}

function wouldHaveFourDaySquare(state: AppState, todayCount: number): boolean {
  if (!state.team || state.team.members.length !== 4 || todayCount !== 4) return false;
  const previousThreeDays = state.days.slice(1, 4);
  return previousThreeDays.length === 3 && previousThreeDays.every((day) => getDoneCountForDay(state, day) === 4);
}

function getYattaSoundCategory(previous: AppState | null, next: AppState): EventSoundCategory | null {
  const oldCount = previous ? getDoneCountForDay(previous, next.todayKey) : 0;
  const newCount = getDoneCountForDay(next, next.todayKey);
  if (newCount <= oldCount) return null;

  const teamSize = next.team?.members.length ?? 1;
  if (teamSize === 4 && newCount === 4 && hasFourDaySquare(next)) return "seihoukei";
  if (teamSize >= 3 && newCount === teamSize) return "perfect";
  if (isBeforeEight()) return "asahayai_yatta";
  return "1yatta";
}

function predictYattaSoundCategory(state: AppState): EventSoundCategory {
  const teamSize = state.team?.members.length ?? 1;
  const currentCount = getDoneCountForDay(state, state.todayKey);
  const predictedCount = currentCount + (state.isTodayDone ? 0 : 1);

  if (teamSize === 4 && wouldHaveFourDaySquare(state, predictedCount)) return "seihoukei";
  if (teamSize >= 3 && predictedCount === teamSize) return "perfect";
  if (isBeforeEight()) return "asahayai_yatta";
  return "1yatta";
}

function getPredictedCelebrationMode(state: AppState, category: EventSoundCategory): FxMode {
  if (category !== "seihoukei" && category !== "perfect") return "big";
  const currentCount = getDoneCountForDay(state, state.todayKey);
  const predictedCount = currentCount + (state.isTodayDone ? 0 : 1);
  return getCelebrationMode(predictedCount, state.teamSize);
}

function getInviteLink(code: string): string {
  return `${window.location.origin}${window.location.pathname}?join=${encodeURIComponent(code)}`;
}

export function App() {
  const [nameReady, setNameReady] = useState(() => Boolean(getStoredDisplayName()));
  const [nameInput, setNameInput] = useState(() => getStoredDisplayName());
  const [state, setState] = useState<AppState | null>(null);
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [pushStatus, setPushStatus] = useState<PushStatus>(() => getPushCapabilityStatus());
  const [pushBusy, setPushBusy] = useState(false);
  const [showMakeup, setShowMakeup] = useState(false);
  const [impact, setImpact] = useState<{ id: number; mode: FxMode } | null>(null);
  const previousCountRef = useRef(0);
  const processedJoinRef = useRef(false);
  const stateRef = useRef<AppState | null>(null);
  const yattaButtonRef = useRef<HTMLButtonElement | null>(null);
  const { canvasRef, burst } = useCanvasFx();
  const inviteLink = state?.team ? getInviteLink(state.team.inviteCode) : "";

  const buttonOrigin = () => {
    const rect = yattaButtonRef.current?.getBoundingClientRect();
    if (!rect) return undefined;
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  const triggerImpact = useCallback((mode: FxMode) => {
    setImpact({ id: Date.now(), mode });
  }, []);

  const setCurrentState = useCallback((next: AppState) => {
    stateRef.current = next;
    previousCountRef.current = next.todayDoneCount;
    setState(next);
  }, []);

  const playCategorizedEvent = useCallback(async (category: EventSoundCategory, mode: FxMode) => {
    burst(mode, buttonOrigin());
    triggerImpact(mode);
    const played = await playEventSound(category);
    if (!played) await playSound(mode);
  }, [burst, triggerImpact]);

  const memberOrder = useMemo(() => {
    if (!state) return [];
    if (state.team) return state.team.members.map((member) => member.profileId);
    return [state.profile.id];
  }, [state]);

  const refresh = useCallback(
    async (detectTeamCelebration = false) => {
      const next = await getState();
      if (detectTeamCelebration && state) {
        const category = getYattaSoundCategory(stateRef.current, next);
        if (category) {
          const mode = category === "seihoukei" || category === "perfect"
            ? getCelebrationMode(getDoneCountForDay(next, next.todayKey), next.teamSize)
            : "big";
          void playCategorizedEvent(category, mode);
        }
      }
      setCurrentState(next);
    },
    [playCategorizedEvent, setCurrentState, state]
  );

  useEffect(() => {
    if (!nameReady) return;
    let cancelled = false;

    ensureProfile()
      .then(() => getState())
      .then((initial) => {
        if (cancelled) return;
        installQueuedClipRetry();
        preloadEventSounds();
        setCurrentState(initial);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "起動に失敗しました");
      });

    return () => {
      cancelled = true;
    };
  }, [nameReady, playCategorizedEvent, setCurrentState]);

  useEffect(() => {
    if (!state) return;
    const interval = window.setInterval(() => {
      refresh(true).catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(interval);
  }, [refresh, state]);

  useEffect(() => {
    if (!state?.team) return;
    refreshPushSubscription()
      .then(setPushStatus)
      .catch(() => setPushStatus(getPushCapabilityStatus()));
  }, [state?.team?.id]);

  const doYatta = async () => {
    if (!state || busy) return;
    void playSound("press");

    if (state.isTodayDone) {
      burst("normal", buttonOrigin());
      triggerImpact("normal");
      await playSound("soft");
      return;
    }

    if (!state.isYesterdayDone && state.makeupAvailable) {
      setShowMakeup(true);
      burst("normal", buttonOrigin());
      triggerImpact("normal");
      return;
    }

    await commitToday();
  };

  const commitToday = async () => {
    setBusy(true);
    setError(null);
    const optimisticState = stateRef.current;
    let playedOptimistic = false;
    if (optimisticState && !optimisticState.isTodayDone) {
      const category = predictYattaSoundCategory(optimisticState);
      const mode = getPredictedCelebrationMode(optimisticState, category);
      playedOptimistic = true;
      void playCategorizedEvent(category, mode);
    }

    try {
      const result = await pressYatta();
      if (result.isNew) {
        const previous = stateRef.current;
        const next = await getState();
        const category = getYattaSoundCategory(previous, next) ?? "1yatta";
        const mode = category === "seihoukei" || category === "perfect"
          ? getCelebrationMode(getDoneCountForDay(next, next.todayKey), next.teamSize)
          : "big";
        setCurrentState(next);
        if (!playedOptimistic) await playCategorizedEvent(category, mode);
      } else {
        await refresh(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "YATTAに失敗しました");
    } finally {
      setBusy(false);
      setShowMakeup(false);
    }
  };

  const commitMakeup = async () => {
    setBusy(true);
    setError(null);
    void playCategorizedEvent("1yatta", "big");
    try {
      const result = await useMakeup();
      if (!result.success) throw new Error(result.error ?? "救済に失敗しました");
      await refresh(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "救済に失敗しました");
    } finally {
      setBusy(false);
      setShowMakeup(false);
    }
  };

  const submitName = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const clean = saveDisplayName(nameInput);
    if (!clean) {
      setError("名前を入れてください");
      return;
    }
    setError(null);
    setNameReady(true);
  };

  const handleCreateTeam = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setCurrentState(await createTeam());
    } catch (err) {
      setError(err instanceof Error ? err.message : "チーム作成に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const handleJoinTeam = async () => {
    if (!inviteCode.trim()) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setCurrentState(await joinTeam(inviteCode.trim()));
      setInviteCode("");
      window.history.replaceState(null, "", window.location.pathname);
    } catch (err) {
      setError(err instanceof Error ? err.message : "参加に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const handleSendFeedback = async () => {
    const body = feedbackText.trim();
    if (!body || sendingFeedback) return;
    setSendingFeedback(true);
    setError(null);
    setNotice(null);
    try {
      const result = await sendFeedback(body);
      if (!result.success) throw new Error(result.error ?? "feedback_failed");
      setFeedbackText("");
      setNotice("フィードバックを送りました。");
    } catch (err) {
      setError(err instanceof Error ? err.message : "フィードバック送信に失敗しました");
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await enablePushNotifications();
      setPushStatus(next);
      if (next === "enabled") setNotice("通知をONにしました。");
      if (next === "unconfigured") setError("通知サーバーの設定がまだです。");
    } catch (err) {
      setError(err instanceof Error ? err.message : "通知ONに失敗しました");
    } finally {
      setPushBusy(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setNotice("招待リンクをコピーしました。");
    } catch {
      setNotice(inviteLink);
    }
  };

  const shareInviteLink = async () => {
    if (!inviteLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "YATTA!",
          text: "このチームでYATTAしよう。",
          url: inviteLink,
        });
        return;
      } catch {
        return;
      }
    }
    await copyInviteLink();
  };

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("join")?.trim().toUpperCase();
    if (!code) return;
    setInviteCode(code);
    if (!state || state.team || processedJoinRef.current) return;

    processedJoinRef.current = true;
    setBusy(true);
    setError(null);
    setNotice("招待リンクから参加中です。");
    joinTeam(code)
      .then((next) => {
        setCurrentState(next);
        setInviteCode("");
        setNotice("チームに参加しました。");
        window.history.replaceState(null, "", window.location.pathname);
      })
      .catch((err) => {
        processedJoinRef.current = false;
        setError(err instanceof Error ? err.message : "参加に失敗しました");
      })
      .finally(() => setBusy(false));
  }, [setCurrentState, state]);

  if (!nameReady) {
    return (
      <main className="boot name-boot">
        <form className="name-form" onSubmit={submitName}>
          <span className="boot-mark">YATTA!</span>
          <label htmlFor="displayName">最初に名前を入れてください。</label>
          <input
            id="displayName"
            value={nameInput}
            onChange={(event) => setNameInput(event.target.value)}
            placeholder="例: Naoki"
            autoComplete="name"
            autoFocus
            maxLength={24}
          />
          <button type="submit">Start</button>
          {error && <span className="boot-line">{error}</span>}
        </form>
      </main>
    );
  }

  if (!state) {
    return (
      <main className="boot">
        <span className="boot-mark">YATTA!</span>
        <span className="boot-line">{error ?? "起動中"}</span>
      </main>
    );
  }

  return (
    <main className="shell">
      <canvas ref={canvasRef} className="fx" aria-hidden="true" />
      {impact && (
        <div key={impact.id} className={`impact impact-${impact.mode}`} aria-hidden="true">
          <span>YATTA!</span>
        </div>
      )}

      <section className="hero">
        <div className="hero-top">
          <p className="kicker">{state.profile.displayName} / {state.todayKey}</p>
          <p className="team-count">
            {state.todayDoneCount}/{state.teamSize}
          </p>
        </div>

        <button
          ref={yattaButtonRef}
          className={`yatta ${state.isTodayDone ? "done" : ""} ${impact ? `blast-${impact.mode}` : ""}`}
          type="button"
          onClick={doYatta}
          disabled={busy}
        >
          YATTA!
        </button>

        <div className="team-strip">
          {state.team ? (
            <>
              <span>CODE</span>
              <strong>{state.team.inviteCode}</strong>
            </>
          ) : (
            <>
              <button type="button" onClick={handleCreateTeam} disabled={busy}>
                Create
              </button>
              <input
                value={inviteCode}
                onChange={(event) => setInviteCode(event.target.value.toUpperCase())}
                placeholder="CODE"
                maxLength={8}
                aria-label="Invite code"
              />
              <button type="button" onClick={handleJoinTeam} disabled={busy || !inviteCode.trim()}>
                Join
              </button>
            </>
          )}
        </div>

        {state.team && (
          <div className="invite-panel">
            <input readOnly value={inviteLink} aria-label="Invite link" />
            <button type="button" onClick={copyInviteLink}>
              Copy
            </button>
            <button type="button" onClick={shareInviteLink}>
              Share
            </button>
          </div>
        )}

        {state.team && (
          <div className={`push-panel push-${pushStatus}`}>
            <div>
              <p className="guide-title">通知</p>
              <p className="push-copy">
                {pushStatus === "enabled" && "他の人のYATTAと4ブロック完成を通知します。"}
                {pushStatus === "needs-install" && "iPhoneはホーム画面に追加したアプリから通知ONできます。"}
                {pushStatus === "unsupported" && "この環境では通知に対応していません。"}
                {pushStatus === "denied" && "通知がブロックされています。端末の設定から許可してください。"}
                {pushStatus === "unconfigured" && "通知サーバーの設定がまだです。"}
                {pushStatus === "default" && "他の人のYATTAと4ブロック完成を受け取れます。"}
              </p>
            </div>
            {pushStatus === "default" || pushStatus === "unconfigured" ? (
              <button type="button" onClick={handleEnablePush} disabled={pushBusy}>
                通知ON
              </button>
            ) : (
              <span>{pushStatus === "enabled" ? "ON" : "OFF"}</span>
            )}
          </div>
        )}

        <div className="guide">
          {state.team ? (
            <>
              <p className="guide-title">チームでやる方法</p>
              <ol>
                <li>招待リンクをCopyかShareで友達に送る。</li>
                <li>友達はリンクを開いてJoin。</li>
                <li>各自が1日1回YATTAを押す。</li>
                <li>下の横1列が今日のチーム状況。全員そろうと特別音。</li>
              </ol>
            </>
          ) : (
            <>
              <p className="guide-title">チーム開始</p>
              <ol>
                <li>最初の1人がCreateを押す。</li>
                <li>出た招待リンクをCopyかShareで友達に送る。</li>
                <li>友達はリンクを開くだけで参加。</li>
                <li>3〜4人で毎日YATTA。状態は約5秒で反映。</li>
              </ol>
            </>
          )}
          <p className="guide-note">
            音: 8時前 / 1人YATTA / 全員完成 / 4人で4日連続完成 / 未完成の翌日で変わります。
          </p>
        </div>

        <div className="feedback-panel">
          <p className="guide-title">フィードバック</p>
          <p className="feedback-copy">{state.profile.displayName} として送信します。</p>
          <textarea
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value)}
            placeholder="使いにくい所、欲しい機能、バグなど"
            maxLength={1000}
            aria-label="Feedback"
          />
          <div className="feedback-actions">
            <span>{feedbackText.trim().length}/1000</span>
            <button type="button" onClick={handleSendFeedback} disabled={sendingFeedback || !feedbackText.trim()}>
              Send
            </button>
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        {notice && <p className="notice">{notice}</p>}
      </section>

      <section className="grid" aria-label="30日グリッド">
        <div className="member-labels" style={{ gridTemplateColumns: `52px repeat(${memberOrder.length}, minmax(0, 1fr))` }}>
          <span />
          {memberOrder.map((profileId) => {
            const member = state.team?.members.find((item) => item.profileId === profileId);
            return <span key={profileId}>{member?.displayName ?? state.profile.displayName}</span>;
          })}
        </div>
        {state.days.map((day) => {
          const count = memberOrder.filter((profileId) => state.statuses[profileId]?.includes(day)).length;
          const mega = memberOrder.length === 4 && count === 4;
          const big = !mega && ((memberOrder.length === 4 && count >= 3) || (memberOrder.length === 3 && count >= 3));

          return (
            <div key={day} className={`row ${day === state.todayKey ? "today" : ""} ${big ? "big" : ""} ${mega ? "mega" : ""}`}>
              <time>{getShortDate(day)}</time>
              <div className="cells" style={{ gridTemplateColumns: `repeat(${memberOrder.length}, minmax(0, 1fr))` }}>
                {memberOrder.map((profileId) => {
                  const lit = state.statuses[profileId]?.includes(day);
                  return (
                    <span
                      key={profileId}
                      className={`cell ${lit ? "lit" : ""} ${profileId === state.profile.id ? "self" : ""}`}
                      title={state.team?.members.find((member) => member.profileId === profileId)?.displayName ?? state.profile.displayName}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {showMakeup && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="sheet">
            <button className="sheet-close" type="button" onClick={() => setShowMakeup(false)}>
              x
            </button>
            <p>昨日も埋めますか。</p>
            <div className="sheet-actions">
              <button type="button" onClick={commitMakeup} disabled={busy}>
                Yesterday
              </button>
              <button type="button" onClick={commitToday} disabled={busy}>
                Today
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
